import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import * as React from 'react';
import styled, { css } from 'styled-components';

import { useAppSelector } from '@/redux/hooks';
import { getCatalogDetailCurators } from '@/redux/modules/catalog/detail/catalogDetail.selectors';
import { getCuratorBaseUrl } from '@/redux/modules/config';
import { Curator as CuratorType } from '@/types/Curator';
import { getCuratorImageUrl } from '@/utils/urls';

import Curator from './Curator';
import Curators from './Curators';

type Props = {
    catalogId: number;
    className?: string;
};

const CatalogCurators = ({ catalogId, className }: Props) => {
    const curators = useAppSelector((state) => getCatalogDetailCurators(state, catalogId)) || [];
    const curatorImgUrl = useAppSelector(getCuratorBaseUrl);
    const multipleCurators = curators.length > 1;

    const CuratorComponent = multipleCurators ? Curators : Curator;
    const size = multipleCurators ? 160 : 280;

    const mapCurators = React.useCallback(
        ({ expertise, id, imageFileName, name }: CuratorType) => {
            return (
                <CuratorComponent
                    expertise={expertise}
                    id={id}
                    imgSrc={getCuratorImageUrl(curatorImgUrl, id, imageFileName, size)}
                    key={`${id}-${name}`}
                    name={name}
                />
            );
        },
        [CuratorComponent, curatorImgUrl, size]
    );

    return curators.length ? (
        <StyledCatalogCurators className={className}>
            <StyledCuratorInfo>
                <StyledLabel $moreThanOneCurator={multipleCurators}>
                    <FormattedMessage id={multipleCurators ? 'auction_curators' : 'auction_curator'} />:
                </StyledLabel>
                <StyledCuratorContainer
                    $moreThanOneCurator={multipleCurators}
                    id="curators-list"
                >
                    {curators.map(mapCurators)}
                </StyledCuratorContainer>
            </StyledCuratorInfo>
        </StyledCatalogCurators>
    ) : null;
};

export default React.memo<Props>(CatalogCurators);

const StyledCatalogCurators = styled.div`
    min-width: 250px;
    border-left: 1px solid ${({ theme }) => theme.colors.grey400};

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        border-left: none;
    }
`;

const StyledCuratorInfo = styled.div`
    margin: 30px 0;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const StyledLabel = styled.span<{ $moreThanOneCurator: boolean }>`
    color: ${({ theme }) => theme.colors.grey100};
    font-family: ${({ theme }) => theme.typography.fontPrimary};
    font-size: 14px;
    letter-spacing: 1px;
    line-height: 20px;
    text-transform: uppercase;
    margin-bottom: 18px;
    ${({ $moreThanOneCurator }) =>
        $moreThanOneCurator
            ? css`
                  align-self: flex-start;
                  margin-left: 20px;
                  margin-left: 25px;

                  @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
                      align-self: center;
                      margin-left: 0;
                      margin-left: 25px;
                  }
              `
            : ''}
`;

const StyledCuratorContainer = styled.div<{ $moreThanOneCurator: boolean }>`
    ${({ $moreThanOneCurator }) =>
        $moreThanOneCurator
            ? css`
                  align-items: stretch;
                  color: ${({ theme }) => theme.colors.black};
                  display: flex;
                  width: 100%;
                  flex-direction: column;
                  justify-content: space-evenly;
                  margin-top: 18px;

                  @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
                      flex-direction: column;
                  }
              `
            : ''}
`;
